/**
 * RestaurantManager — Thin orchestrator for restaurant UI.
 *
 * Reuses ecommerce cart flow (product_selected / cart_updated) for
 * unified backend handling. Delegates all UI to the active presenter.
 */
import { RestaurantDesktopPresenter } from './RestaurantDesktopPresenter.js';
import { RestaurantMobilePresenter } from './RestaurantMobilePresenter.js';

export class RestaurantManager {
  constructor(config = {}) {
    this.shadowRoot = config.shadowRoot;
    this.primaryColor = config.primaryColor || '#2563eb';
    this.onItemSelected = config.onItemSelected || (() => {});

    this.cartItemCount = 0;
    this.cartTotal = 0;
    this.currencySymbol = '$';

    this._presenter = null;
  }

  get presenter() {
    if (!this._presenter) {
      this._presenter = this._createPresenter();
    }
    return this._presenter;
  }

  _createPresenter() {
    if (this._isMobileCall()) {
      return new RestaurantMobilePresenter({
        primaryColor: this.primaryColor,
        onItemSelect: (item) => this._handleItemSelect(item),
      });
    }

    return new RestaurantDesktopPresenter({
      shadowRoot: this.shadowRoot,
      primaryColor: this.primaryColor,
      onItemSelect: (item) => this._handleItemSelect(item),
    });
  }

  _isMobileCall() {
    return !!document.getElementById('mobile-voice-call-bar-container');
  }

  handleShowItems(message) {
    const items = message.items || [];
    if (!items.length) {
      console.warn('[RestaurantManager] show_items with no items');
      return;
    }

    const menuItems = items.map(mapMenuItem);
    this.presenter.showMenuItems(menuItems, message.title);
  }

  handleCartUpdated(message) {
    const { product, action, cartTotal, cartItemCount } = message;

    if (cartTotal !== undefined && cartItemCount !== undefined) {
      this.cartItemCount = cartItemCount;
      this.cartTotal = cartTotal;
      this.currencySymbol = this._resolveCurrency(message.currency);

      this.presenter.updateOrder({
        orderItemCount: cartItemCount,
        orderTotal: cartTotal,
      });
    }

    if (action === 'added' && product) {
      this.presenter.onItemAdded?.(product);
    }
  }

  _resolveCurrency(currency) {
    if (!currency) return this.currencySymbol || '$';
    return currency === 'ILS' ? '₪' : '$';
  }

  hideMenuItems() {
    this.presenter.hideMenuItems();
  }

  _buildCartInfo() {
    return {
      itemCount: this.cartItemCount,
      total: this.cartTotal,
      currency: this.currencySymbol,
    };
  }

  _handleItemSelect(item) {
    const price = item.price || 0;
    const qty = item.selectedAmount || 1;
    this.currencySymbol = this._resolveCurrency(item.currency);

    this.cartItemCount += 1;
    this.cartTotal += price * qty;

    this.presenter.onItemAdded(item, this._buildCartInfo());
    this.onItemSelected(item);
  }

  destroy() {
    if (this._presenter) {
      this._presenter.destroy();
      this._presenter = null;
    }
  }
}

function mapMenuItem(raw) {
  return {
    id: raw.itemId || raw.id,
    name: raw.name || raw.itemName || '',
    category: raw.category || '',
    price: raw.price || 0,
    currency: raw.currency || 'USD',
    imageUrl: raw.imageUrl || raw.image_url || '',
    available: raw.available !== undefined ? raw.available : true,
    tags: raw.tags || [],
    allergens: raw.allergens || [],
    description: raw.description || '',
    orderQuantity: raw.orderQuantity || raw.order_quantity || 0,
  };
}
